import _ from 'lodash';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import API from '../../../app/api';
import useWardTrudMatrix from '../../../hooks/useWardTrudMatrix';
import {BMDestination, setRefreshFalse} from '../../../store/reducers/bedManagement';
import {setSnackbar} from '../../../store/reducers/globals';
import {getWardsIdForPatientsSearch} from '../../../store/reducers/wardMap';
import {hydrateBeds} from '../bmUtils';
import useBays from './useBays';
import useBeds from './useBeds';
import useRequests from './useRequests';

const useHydratedBeds = ({selectedHospitalId, selectedWard, selectedBay, allRequests, filter, sort, sortDirection}) => {
    const dispatch = useDispatch();
    const controller = new AbortController();

    const isDestination = useSelector(BMDestination);

    const [wardsList] = useWardTrudMatrix();
    const [bays] = useBays(selectedWard, selectedHospitalId);
    const [__, ___, returnedData] = useBeds(selectedWard, wardsList, bays);
    const [requests, ____, allReqs] = useRequests(selectedWard, allRequests);

    const [bayFiltered, setBayFiltered] = useState([]); // If selected bay is passed and is not empty, it will filter hydrated beds by passed bay id;
    const [hydratedBeds, setHydratedBeds]  = useState([]);
    const [bedsLoadingStatus, setBedsLoadingStatus] = useState(true);

    const getStatuses = useCallback(async (hospital, ward) => {
        setBedsLoadingStatus(true);
        try {
            let wardId = ward;
            if (hospital) {
                wardId = dispatch(getWardsIdForPatientsSearch([ward], hospital));
            }
            const data = await API.getWardData(_.isArray(wardId) ? wardId[0] : wardId);
            return data?.bedStatuses;
        }
        catch (error) {
            dispatch(setSnackbar({type: 'error', message: `Cannot load bed statuses for ward ${ward}`}));
        } finally {
            setBedsLoadingStatus(false);
        }
    }, []);

    const isObjectAndHasKeys = useCallback((obj) => _.isObject(obj) && !_.isEmpty(obj), []);
    const isNotEmptyArr = useCallback((arr) => _.isArray(arr) && !_.isEmpty(arr), []);

    const handleHydrateBeds = useCallback(async (hospital, ward, beds, reqs) => {
        setBedsLoadingStatus(true);

        try {
            if (ward && reqs) {
                const statuses = await getStatuses(hospital, ward);
                let mappedBeds = hydrateBeds(beds, statuses, reqs, ward);

                if (!isNotEmptyArr(filter) || _.isString(filter) || isObjectAndHasKeys(filter)) {
                    mappedBeds = _.filter(mappedBeds, filter);
                }

                if (!_.isEmpty(sort) && !_.isEmpty(hydratedBeds)) {
                    mappedBeds = _.orderBy(mappedBeds, sort, sortDirection);
                }

                setHydratedBeds(mappedBeds);
                return mappedBeds
            }
        } catch (err) {
            console.log("ERROR:", err);
        } finally {
            setBedsLoadingStatus(false);
        }
    }, []);

    useEffect(() => {
        if (!!selectedBay && !_.isEmpty(hydratedBeds)) {
            const filtered = _.filter(hydratedBeds, b => b?.bay?.id === selectedBay);
            setBayFiltered(filtered);
        } else {
            setBayFiltered(hydratedBeds);
        }
    }, [selectedBay, hydratedBeds]);

    useEffect(() => {
        if (selectedWard) {
            handleHydrateBeds(selectedHospitalId, selectedWard, returnedData, allRequests ? allReqs : requests);
            return () => {
                controller.abort();
                dispatch(setRefreshFalse());
            };
        }
    }, [selectedHospitalId, selectedWard, requests, allReqs, returnedData, isDestination]);

    return {hydratedBeds, bayFiltered, bedsLoadingStatus, requests, returnedData};
}

// SORT OBJ:
// {
//     path: 'xxx', (string)
//     direction: 'asc' (string => 'asc' or 'desc')
// }

useHydratedBeds.propTypes = {
    selectedWard: PropTypes.string.isRequired,
    selectedBay: PropTypes.string,
    allRequests: PropTypes.bool,
    filter: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
    sort: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    sortDirection: PropTypes.oneOf(['asc', 'desc']),
};

useHydratedBeds.defaultProps = {
    selectedBay: '',
    allRequests: false,
    filter: {},
    sort: {},
    sortDirection: 'asc',
};

export default useHydratedBeds;